import { useState } from "react"
import Icon from "@/v2-ui/icon"
import Tooltip from "@/v2-ui/tooltip"
import Button from "@/v2-backoffice-client/components/button"
import TenantActivationStatusBadge from "@/v2-backoffice-client/tenant/migration/TenantActivationStatusBadge"
import UserMigrateDialog from "@/v2-backoffice-client/user/migration/UserMigrateDialog"
import {
  ACTIVATION_STATUS_NOT_MIGRATED,
  canSendActivation,
  getActivationExperienceDescription,
  getActivationExperienceLabel,
  getActivationStatusStyle,
} from "@/v2-backoffice-client/tenant/migration/tenantMigration.constants"
import type { UserFragment } from "@/v2-backoffice-client/user/__types__/user.fragment"

export type UserMigrationCellProps = {
  user: UserFragment
  /**
   * The tenant whose wave an individual send should attribute to. When
   * omitted, UserMigrateDialog resolves it from the user's memberships
   * (a single membership resolves silently; several show a picker; none
   * disables the action — activations are tenant-scoped).
   */
  tenantId?: number
}

/**
 * The Fusion-login status badge with its action attached (FUS-927): a rich
 * tooltip explaining the status + which onboarding journey the user gets,
 * and — for the states where it helps — a button that migrates THIS user
 * (send or resend the activation) via UserMigrateDialog.
 *
 * Same architecture as UserIdentityCell: the button lives in the hoverable
 * tooltip, the dialog it opens is a SIBLING of the badge (tooltip content
 * unmounts on close; a dialog inside it would vanish mid-flight).
 */
function UserMigrationCell(props: UserMigrationCellProps) {
  const { user, tenantId } = props
  // The dialog's open-state IS the label it was opened with: the send's
  // response can flip the row to a state with no action (e.g. skipped —
  // already active), and a `canAct`-gated mount would vanish mid-outcome.
  const [openedWithLabel, setOpenedWithLabel] = useState<string>()

  const status = user.MigrationStatus
  const style = getActivationStatusStyle(status)
  const actionLabel =
    status === ACTIVATION_STATUS_NOT_MIGRATED
      ? "Send activation"
      : canSendActivation(status)
        ? "Resend activation"
        : undefined
  const canAct =
    actionLabel !== undefined &&
    (tenantId !== undefined || (user.Tenants ?? []).length > 0)

  const experienceLabel = getActivationExperienceLabel(
    user.ActivationExperience,
  )

  return (
    <>
      <Tooltip>
        <Tooltip.Trigger asChild>
          <span className="inline-flex">
            <TenantActivationStatusBadge
              status={status}
              experience={user.ActivationExperience}
              suppressTitle
            />
          </span>
        </Tooltip.Trigger>

        <Tooltip.Content side="top" className="ui-tooltip-content--panel">
          <div className="flex max-w-xs flex-col gap-3 p-3">
            <div className="flex flex-col gap-1">
              <span className="flex items-center gap-1.5 font-medium text-popover-foreground">
                <Icon
                  className={`shrink-0 ${style.iconColor}`}
                  src={style.iconSrc}
                  size={14}
                />
                {style.label}
              </span>
              <p className="text-muted-foreground">{style.description}</p>
              {experienceLabel ? (
                <p className="mt-1 text-xs text-muted-foreground">
                  <span className="font-medium text-popover-foreground">
                    {experienceLabel} journey:
                  </span>{" "}
                  {getActivationExperienceDescription(
                    user.ActivationExperience,
                  )}
                </p>
              ) : null}
            </div>

            {canAct && (
              <div className="border-t border-border pt-2">
                <Button
                  variant="outline"
                  size="xs"
                  onClick={() => setOpenedWithLabel(actionLabel)}
                >
                  {actionLabel}
                </Button>
              </div>
            )}
          </div>
        </Tooltip.Content>
      </Tooltip>

      {openedWithLabel && (
        <UserMigrateDialog
          user={user}
          tenantId={tenantId}
          actionLabel={openedWithLabel}
          open
          onOpenChange={(next) => {
            if (!next) setOpenedWithLabel(undefined)
          }}
        />
      )}
    </>
  )
}

export default UserMigrationCell
